import { useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Button, Input, Popover, PopoverContent, PopoverTrigger, cn } from '@staff-app/ui';
import { formatHoursMinutes } from '../lib/duration';

/** Sentinel for "my own entries". Kept out of the email namespace so it can never collide. */
export const ME = '__me__';

export interface AgentOption {
  email: string;
  /** Minutes logged in the month being viewed, when known. Undefined = has not logged any. */
  minutes?: number;
}

interface AgentSelectProps {
  value: string;
  onChange: (value: string) => void;
  agents: AgentOption[];
  loading?: boolean;
}

/**
 * Root's "whose time am I looking at" picker — a searchable combobox.
 *
 * Built from Popover + Input rather than the `Select` used elsewhere on these pages, because a
 * Radix Select has no filtering and the roster is the whole support team, not a handful of
 * options. There is no `cmdk` in this repo, so the list is plain buttons over a filtered array —
 * enough for a few dozen names, and it avoids adding a dependency for one control.
 *
 * The list is the STAFF ROSTER, not just the agents who happen to have logged time this month.
 * That distinction is the point of the search box: an agent with an empty month is exactly the
 * one you go looking for, and building the list from the rollup would hide them. Agents who DO
 * have time show their total beside the name, so the useful ones are still obvious at a glance.
 */
export function AgentSelect({ value, onChange, agents, loading }: AgentSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter((a) => a.email.toLowerCase().includes(q));
  }, [agents, query]);

  // "My time" stays reachable by typing 'me' or 'my' as well as by being pinned at the top.
  const showMe = !query.trim() || 'my time me'.includes(query.trim().toLowerCase());

  const label = value === ME ? 'My time' : value;

  function pick(next: string): void {
    onChange(next);
    setOpen(false);
    setQuery('');
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery('');
      }}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-[260px] justify-between font-normal"
          data-testid="opticlock-agent-select"
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>

      <PopoverContent className="w-[300px] p-0" align="start">
        <div className="flex items-center gap-2 border-b px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 opacity-50" />
          <Input
            ref={inputRef}
            autoFocus
            value={query}
            placeholder="Search staff…"
            aria-label="Search staff"
            data-testid="opticlock-agent-search"
            className="h-7 border-0 px-0 shadow-none focus-visible:ring-0"
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              // Enter takes the first match — the whole point of typing is to not have to aim.
              if (e.key === 'Enter') {
                e.preventDefault();
                if (filtered.length > 0) pick(filtered[0].email);
                else if (showMe) pick(ME);
              }
            }}
          />
        </div>

        <div className="max-h-[280px] overflow-y-auto p-1">
          {showMe ? (
            <AgentRow
              testId="opticlock-agent-option-me"
              label="My time"
              selected={value === ME}
              onSelect={() => pick(ME)}
            />
          ) : null}

          {filtered.map((a) => (
            <AgentRow
              key={a.email}
              testId={`opticlock-agent-option-${a.email}`}
              label={a.email}
              hint={a.minutes !== undefined ? formatHoursMinutes(a.minutes) : undefined}
              selected={value === a.email}
              onSelect={() => pick(a.email)}
            />
          ))}

          {loading && agents.length === 0 ? (
            <p className="px-2 py-3 text-xs text-muted-foreground">{'Loading staff…'}</p>
          ) : null}

          {!loading && filtered.length === 0 && !showMe ? (
            <p className="px-2 py-3 text-xs text-muted-foreground" data-testid="opticlock-agent-empty">
              {`Nobody matches “${query.trim()}”`}
            </p>
          ) : null}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function AgentRow({
  testId,
  label,
  hint,
  selected,
  onSelect,
}: {
  testId: string;
  label: string;
  hint?: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={selected}
      data-testid={testId}
      onClick={onSelect}
      className={cn(
        'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm',
        'hover:bg-panel-hover focus-visible:bg-panel-hover focus-visible:outline-none',
        selected && 'bg-sidebar-active',
      )}
    >
      <Check className={cn('h-3.5 w-3.5 shrink-0', selected ? 'opacity-100' : 'opacity-0')} />
      <span className="grow truncate">{label}</span>
      {hint ? <span className="shrink-0 tabular-nums text-xs text-muted-foreground">{hint}</span> : null}
    </button>
  );
}
